import { useEffect } from 'react'

import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getDocumentDescription } from '../lib/helpers'
import { selectUserDocumentCategoryByDocument } from '../lib/userDocuments.selector'
import { UserDocument } from '../lib/userDocuments.slice'
import { UserDocumentTag } from '../shared/UserDocumentTag'
import { DateUploadedCell } from './DateUploadedCell'
import { FileNameCell } from './FileNameCell'
import { ActionCell } from './ActionCell'
import { MultipleFinancialAccountCell } from './MultipleFinancialAccountCell'

import { Table } from '@/ui'

export interface RowComponentProps {
  document: UserDocument
  onCheck?: (docId: number, checked: boolean) => void
  editable?: boolean
}

export const BankStatementRow = ({ document }: RowComponentProps) => {
  const dispatch = useAppDispatch()
  const category = useReselector(selectUserDocumentCategoryByDocument, document)

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch])
  return (
    <Table.Row className={`${document.id === -1 && 'required-user-document'}`}>
      <DateUploadedCell document={document} />
      <FileNameCell category={category} document={document} />
      <Table.Cell>
        <UserDocumentTag document={document} />
      </Table.Cell>
      <MultipleFinancialAccountCell document={document} />
      <ActionCell document={document} />
    </Table.Row>
  )
}

export const TaxOrIncorporationRow = ({ document }: RowComponentProps) => {
  const category = useReselector(selectUserDocumentCategoryByDocument, document)
  if (document.id === -1 && category?.archivedAt) {
    return null
  }
  return (
    <Table.Row className={`${document.id === -1 && 'required-user-document'}`}>
      <FileNameCell category={category} document={document} />
      <Table.Cell>{getDocumentDescription(document, category)}</Table.Cell>
      <Table.Cell>
        <UserDocumentTag category={category} document={document} />
      </Table.Cell>
      <ActionCell document={document} />
    </Table.Row>
  )
}

export const OtherRow = ({ document }: RowComponentProps) => {
  const category = useReselector(selectUserDocumentCategoryByDocument, document)
  return (
    <Table.Row className={`${document.id === -1 && 'required-user-document'}`}>
      <DateUploadedCell document={document} />
      <FileNameCell category={category} document={document} />
      <Table.Cell>{document.customerDescription}</Table.Cell>
      <ActionCell document={document} />
    </Table.Row>
  )
}

export const ReceiptRow = ({ document }: RowComponentProps) => {
  const category = useReselector(selectUserDocumentCategoryByDocument, document)

  return (
    <Table.Row className={`${document.id === -1 && 'required-user-document'}`}>
      <DateUploadedCell document={document} />
      <FileNameCell category={category} document={document} />
      <Table.Cell />
      <Table.Cell />
    </Table.Row>
  )
}

const RowComponentMap = {
  bankStatement: BankStatementRow,
  bookkeeping: TaxOrIncorporationRow,
  tax: TaxOrIncorporationRow,
  incorporation: TaxOrIncorporationRow,
  other: OtherRow,
  receipt: ReceiptRow,
}

export enum RowTypes {
  BANK_STATEMENT = 'bankStatement',
  BOOKKEEPING = 'bookkeeping',
  TAX = 'tax',
  INCORPORATION = 'incorporation',
  RECEIPT = 'receipt',
  OTHER = 'other',
}

interface TableRowProps {
  document: UserDocument
  onCheck?: (docId: number, checked: boolean) => void
  editable?: boolean
  type?: RowTypes
}

export const TableRow = ({
  document,
  onCheck,
  editable = false,
  type = RowTypes.OTHER,
}: TableRowProps) => {
  const RowComponent = RowComponentMap[type] || OtherRow

  return (
    <RowComponent document={document} onCheck={onCheck} editable={editable} />
  )
}
